import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "../styles/Header.scss";
import logo from "../images/logo-new.png";
import academyLogo from "../images/academy-logo-small.png";
import { FaInstagram, FaWhatsapp, FaBars, FaTimes } from "react-icons/fa";
import { useFooterData } from "../Apis/useApiQueries";
import { FaChevronDown, FaChevronUp } from "react-icons/fa"; // Import arrow icons
import { metaPixelEvents } from "../utils/metaPixel";
import { scrollToTop } from "../utils/scrollToTop";

const Header = () => {
  const location = useLocation();
  const isAcademyPage = location.pathname === "/academy";
  const [logoHeight, setLogoHeight] = useState(50);
  const [logoWidth, setLogoWidth] = useState(70);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
  const [openMenu, setOpenMenu] = useState(null); // מצבו של התפריט הפתוח

  const {
    treatments,
    treatmentsRefs,
    siteDetails,
    error: footerError,
  } = useFooterData();

  // Check if the essential data is loading (only first time)
  const isLoading =
    (treatments.isLoading || treatmentsRefs.isLoading) &&
    !treatments.data &&
    !treatmentsRefs.data;

  useEffect(() => {
    // Allow scrolling when menu is open
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Set initial logo width based on page and screen size
  useEffect(() => {
    const initialWidth = isAcademyPage ? 70 : (isDesktop ? 220 : 70);
    setLogoWidth(initialWidth);
  }, [isAcademyPage, isDesktop]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setLogoHeight(35);
        // Use 220px width for desktop non-Academy pages, keep 55px for Academy
        setLogoWidth(isAcademyPage ? 55 : (isDesktop ? 220 : 55));
      } else {
        setLogoHeight(50);
        // Use 220px width for desktop non-Academy pages, keep 70px for Academy
        setLogoWidth(isAcademyPage ? 70 : (isDesktop ? 220 : 70));
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isAcademyPage, isDesktop]);

  // Show a loading state only if it's the initial load and no cached data exists
  if (isLoading && !treatments.data && !treatmentsRefs.data) {
    return (
      <header className="header">
        <div className="header-loading">
          <p>Loading...</p>
        </div>
      </header>
    );
  }

  // 🔹 FIX: Ensure treatmentsRefs is an array before using forEach
  const createDropDownData = () => {
    const dropDownData = {};

    if (Array.isArray(treatmentsRefs.data)) {
      treatmentsRefs.data.forEach((ref) => {
        dropDownData[ref.treatmentRef] = [];
      });
    }

    if (Array.isArray(treatments.data)) {
      treatments.data.forEach((treatment) => {
        const refKey = treatment.treatmentRef || treatment.name;
        if (!dropDownData[refKey]) {
          dropDownData[refKey] = [];
        }
        dropDownData[refKey].push(treatment.name);
      });
    }

    return dropDownData;
  };

  const dropDownData = createDropDownData();

  if (footerError) {
    return (
      <p className="header-error">
        Error loading header data: {footerError.message}
      </p>
    );
  }

  const toggleMenu = (menu) => {
    if (openMenu === menu) {
      setOpenMenu(null); // אם התפריט פתוח כבר, נסגור אותו
    } else {
      setOpenMenu(menu); // נפתח את התפריט שנבחר
    }
  };

  // Function to handle scroll to top for both desktop and mobile menu clicks
  const handleNavClick = () => {
    scrollToTop();
  };

  // Function to handle mobile menu clicks (close menu and scroll to top)
  const handleMobileMenuClick = () => {
    setMobileMenuOpen(false);
    scrollToTop();
  };

  return (
    <header className="header">
      <div className="contact-buttons">
        <a
          href="https://www.instagram.com/dr_shay_telem/"
          className="contact-button instagram-button"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => metaPixelEvents.trackPhoneClick()}
        >
          <FaInstagram fill="#01727a" size={24} />
          <span>עקבו באינסטגרם</span>
        </a>
        <a
          href="https://api.whatsapp.com/message/LNFLYSQDUAFWK1?autoload=1&app_absent=0&utm_source=ig"
          className="contact-button whatsapp-button"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => metaPixelEvents.trackPhoneClick()}
        >
          <FaWhatsapp fill="#01727a" size={26} />
          <span>דברו איתנו בוואטסאפ</span>
        </a>
      </div>

      {/* Main Navigation */}
      <nav
        className={`nav-links ${isMobileMenuOpen ? "mobile-open" : ""}`}
        onClick={() => setMobileMenuOpen(false)}
      >
        {/* Mobile Menu Logo - Only render on mobile */}
        {!isDesktop && (
          <div className="mobile-menu-logo">
            {isAcademyPage ? (
              <div>
                <img src={academyLogo} alt="Academy Logo" />
              </div>
            ) : (
              <Link to="/" onClick={handleMobileMenuClick}>
                <img src={logo} alt="Logo" />
              </Link>
            )}
          </div>
        )}

        <Link
          to="/"
          className="nav-link"
          onClick={isDesktop ? handleNavClick : handleMobileMenuClick}
        >
          דף הבית
        </Link>
        <Link
          to="/about"
          className="nav-link"
          onClick={isDesktop ? handleNavClick : handleMobileMenuClick}
        >
          עלינו
        </Link>
        <Link
          to="/academy"
          className="nav-link"
          onClick={isDesktop ? handleNavClick : handleMobileMenuClick}
        >
          האקדמיה
        </Link>

        {/* לפני ואחרי */}
        {isDesktop ? (
          <div className="nav-link dropdown">
            לפני ואחרי
            <div className="dropdown-content">
              {Object.keys(dropDownData).map((category) => (
                <div key={category} className="dropdown-item">
                  <Link
                    to={`/before-and-after/${category}`}
                    dir="auto"
                    onClick={() => {
                      setMobileMenuOpen(false); // סגירת התפריט
                      handleNavClick(); // גלילה לראש הדף
                    }}
                  >
                    {category}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div
            className="nav-link dropdown-toggle"
            onClick={(e) => {
              e.stopPropagation();
              toggleMenu("beforeAfter");
            }}
          >
            <span className="nav-text">לפני ואחרי</span>
            {openMenu === "beforeAfter" && (
              <div className="submenu submenu-grid">
                {Object.keys(dropDownData).map((category) => (
                  <div key={category} className="submenu-item">
                    <Link
                      to={`/before-and-after/${category}`}
                      dir="auto"
                      onClick={handleMobileMenuClick}
                    >
                      {category}
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* הטיפולים שלנו */}
        {isDesktop ? (
          <div className="nav-link dropdown">
            הטיפולים שלנו
            <div className="dropdown-content">
              {Object.keys(dropDownData).map((category) => {
                const subItems = dropDownData[category];
                const hasSubItems = subItems.length > 1;
                return (
                  <div key={category} className="dropdown-item">
                    <Link
                      to={
                        hasSubItems
                          ? `/treatments/${category}`
                          : `/treatment/${category}`
                      }
                      dir="auto"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        handleNavClick(); // גלילה לראש הדף
                      }}
                    >
                      {category}
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div
            className="nav-link dropdown-toggle"
            onClick={(e) => {
              e.stopPropagation();
              toggleMenu("treatments");
            }}
          >
            <span className="nav-text">הטיפולים שלנו</span>
            {openMenu === "treatments" && (
              <div className="submenu submenu-grid">
                {Object.keys(dropDownData).map((category) => {
                  const subItems = dropDownData[category];
                  const hasSubItems = subItems.length > 1;
                  return (
                    <div key={category} className="submenu-item">
                      <Link
                        to={
                          hasSubItems
                            ? `/treatments/${category}`
                            : `/treatment/${category}`
                        }
                        dir="auto"
                        onClick={handleMobileMenuClick}
                      >
                        {category}
                      </Link>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <Link
          to="/contact-us"
          className="nav-link"
          onClick={isDesktop ? handleNavClick : handleMobileMenuClick}
        >
          צור קשר
        </Link>

        {/* Mobile menu decorations */}
        {!isDesktop && (
          <>
            <div className="nav-links-bottom-decoration"></div>
            <div className="nav-links-top-decoration"></div>
            <div className="nav-links-side-decoration"></div>
          </>
        )}
      </nav>

      {/* Mobile menu toggle */}
      <div
        className={`hamburger-menu ${isMobileMenuOpen ? "open" : ""}`}
        onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
      >
        <div className="icon-container">
          <FaBars
            size={24}
            className={`hamburger-icon ${
              isMobileMenuOpen ? "hidden" : "visible"
            }`}
          />
          <FaTimes
            size={24}
            className={`x-icon ${isMobileMenuOpen ? "visible" : "hidden"}`}
          />
        </div>
      </div>

      <span className="logo">
        {isAcademyPage ? (
          <div style={{ cursor: 'default' }}>
            <img
              style={{ height: `${logoHeight}px`, width: `${logoWidth}px` }}
              src={academyLogo}
              alt="Academy Logo"
            />
          </div>
        ) : (
          <Link to="/" onClick={handleNavClick}>
            <img
              style={{ height: `${logoHeight}px`, width: `${logoWidth}px` }}
              src={logo}
              alt="Logo"
            />
          </Link>
        )}
      </span>
    </header>
  );
};

export default Header;
